import React, { useMemo } from "react";

/**
 * SpiderChart — static inline-SVG radar chart for the agency landing page.
 *
 * 8 axes matching the 8 commercial pillars, with a fixed "relatively high"
 * set of demo scores. Pure SVG, no dependencies, brand colours only.
 */

const AXES = [
  "Strategy",
  "Marketing",
  "Sales",
  "CRM",
  "Team",
  "Pricing",
  "BD",
  "After-Sales",
];

const SCORES = [82, 88, 76, 71, 85, 79, 90, 84];

const SIZE = 460;
const CX = SIZE / 2;
const CY = SIZE / 2;
const R = 150;
const RINGS = 5;
const LABEL_OFFSET = 32;

const polarToCartesian = (radius, angleRad) => ({
  x: CX + radius * Math.cos(angleRad),
  y: CY + radius * Math.sin(angleRad),
});

const SpiderChart = () => {
  const angles = useMemo(
    () => AXES.map((_, i) => (-Math.PI / 2) + (i * 2 * Math.PI) / AXES.length),
    []
  );

  const dataPoints = SCORES.map((score, i) => {
    const radius = (score / 100) * R;
    return polarToCartesian(radius, angles[i]);
  });
  const dataPath = dataPoints.map((p) => `${p.x},${p.y}`).join(" ");

  const avg = Math.round(SCORES.reduce((a, b) => a + b, 0) / SCORES.length);

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-auto select-none"
        aria-label="Diagnostic maturity radar chart"
      >
        <rect width={SIZE} height={SIZE} fill="#05070d" rx="8" />

        <defs>
          <pattern id="ral-spider-dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.6" fill="#9fc6ff" opacity="0.18" />
          </pattern>
          <radialGradient id="ral-spider-fill" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#9fc6ff" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#9fc6ff" stopOpacity="0.18" />
          </radialGradient>
        </defs>
        <rect width={SIZE} height={SIZE} fill="url(#ral-spider-dots)" rx="8" />

        {Array.from({ length: RINGS }).map((_, i) => {
          const ringRadius = ((i + 1) / RINGS) * R;
          const points = angles
            .map((a) => {
              const p = polarToCartesian(ringRadius, a);
              return `${p.x},${p.y}`;
            })
            .join(" ");
          return (
            <polygon
              key={`ring-${i}`}
              points={points}
              fill="none"
              stroke="#ffffff"
              strokeOpacity={0.1 + i * 0.025}
              strokeWidth="1"
            />
          );
        })}

        {angles.map((a, i) => {
          const end = polarToCartesian(R, a);
          return (
            <line
              key={`spoke-${i}`}
              x1={CX}
              y1={CY}
              x2={end.x}
              y2={end.y}
              stroke="#ffffff"
              strokeOpacity="0.18"
              strokeWidth="1"
            />
          );
        })}

        <polygon
          points={dataPath}
          fill="url(#ral-spider-fill)"
          stroke="#9fc6ff"
          strokeWidth="2"
        />

        {dataPoints.map((p, i) => (
          <circle
            key={`pt-${i}`}
            cx={p.x}
            cy={p.y}
            r={4}
            fill="#05070d"
            stroke="#9fc6ff"
            strokeWidth="2"
          />
        ))}

        {dataPoints.map((p, i) => {
          const offset = polarToCartesian(
            (SCORES[i] / 100) * R + 14,
            angles[i]
          );
          return (
            <text
              key={`score-${i}`}
              x={offset.x}
              y={offset.y}
              fill="#9fc6ff"
              fontSize="11"
              fontFamily="Helvetica, Arial, sans-serif"
              fontWeight="600"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {SCORES[i]}
            </text>
          );
        })}

        {AXES.map((label, i) => {
          const pos = polarToCartesian(R + LABEL_OFFSET, angles[i]);
          return (
            <text
              key={`label-${i}`}
              x={pos.x}
              y={pos.y}
              fill="#C4CDD9"
              fontSize="13"
              fontFamily="Georgia, serif"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {label}
            </text>
          );
        })}

        <text
          x={CX}
          y={CY - 8}
          fill="#9fc6ff"
          fontSize="11"
          fontFamily="Helvetica, Arial, sans-serif"
          letterSpacing="2"
          textAnchor="middle"
        >
          OVERALL
        </text>
        <text
          x={CX}
          y={CY + 14}
          fill="#ffffff"
          fontSize="28"
          fontFamily="Georgia, serif"
          fontWeight="700"
          textAnchor="middle"
        >
          {avg}
        </text>
      </svg>
    </div>
  );
};

export default SpiderChart;
